import * as R from 'remeda';
import z from 'zod';
import type {} from './libs/vite.ts';
import { EXTENSION_ID, EXTENSION_ORIGIN } from './constants.ts';

export const stringToInt = z.codec(z.string().regex(z.regexes.integer), z.int(), {
  decode: (str) => Number.parseInt(str, 10),
  encode: (num) => num.toString(),
});

const DEFAULT_TORO_ENV = 'dev';
export const ToroEnvironment = z.enum(['local', 'dev', 'prod', 'test']).default(DEFAULT_TORO_ENV);
export type ToroEnvironment = z.infer<typeof ToroEnvironment>;

const loadToroEnvironment = () => {
  // Handle different process.env handling for Node/Next.js/Vite entrypoints
  const getters = [
    () => process.env.TORO_ENV,
    () => process.env.NEXT_PUBLIC_ENV,
    () => import.meta.env?.TORO_ENV,
  ];

  try {
    let rawToroEnv: unknown = undefined;
    for (const getter of getters) {
      try {
        rawToroEnv ||= getter();
      } catch {
        // Swallow errors since some are expected not to work depending on environment
      }
    }
    return ToroEnvironment.parse(rawToroEnv);
  } catch (error) {
    console.warn('Error loading TORO_ENV: ', error);
    console.warn(`Falling back to default TORO_ENV: "${DEFAULT_TORO_ENV}"`);
    return DEFAULT_TORO_ENV;
  }
};

export const toroEnvironment = loadToroEnvironment();
export const isLocal = toroEnvironment === 'local';
export const isProduction = toroEnvironment === 'prod';
export const isTestEnvironment = toroEnvironment === 'test';

export const CommonEnv = z.object({
  TORO_ENV: ToroEnvironment,
  NODE_ENV: z.enum(['development', 'production', 'test']).default('production'),
  TORO_LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),
  TORO_PRETTY_LOGS: z.stringbool().default(false),
  TORO_GCLOUD_PROJECT: z.string().default('webhookprocessor'),

  MONGOOSE_DEBUG: z.stringbool().default(false),
  MAX_FILE_UPLOAD_SIZE_MB: stringToInt.default(10).describe('Max size for user file uploads'),
});
export type CommonEnv = z.infer<typeof CommonEnv>;

export type DefaultsByEnv<T extends object> = Partial<
  Record<ToroEnvironment, Partial<Record<keyof T, string>>>
>;

// Adds the appropriate defaults to process.env, then parses the result
export function parseEnvWithDefaults<Z extends z.ZodObject>(
  schema: Z,
  allDefaults: DefaultsByEnv<z.infer<Z>>
) {
  try {
    const processEnv = typeof process !== 'undefined' ? process?.env : null;
    const defaults = allDefaults[toroEnvironment];
    if (defaults && processEnv) {
      R.forEachObj(defaults, (val, key) => {
        processEnv[key] ||= val;
      });
    }
    return schema.parse(processEnv ?? defaults ?? {});
  } catch (error) {
    const err = new Error('Error parsing process.env', { cause: error });
    console.error(err);
    throw err;
  }
}

const defaultsByEnv: DefaultsByEnv<CommonEnv> = {
  local: {
    NODE_ENV: 'development',
    TORO_LOG_LEVEL: 'debug',
    TORO_PRETTY_LOGS: 'true',
    MONGOOSE_DEBUG: 'false',
  },
} as const;

const parsedEnv = parseEnvWithDefaults(CommonEnv, defaultsByEnv);
export default parsedEnv;

export const logLevel = parsedEnv.TORO_LOG_LEVEL;
export const prettyLogs = parsedEnv.TORO_PRETTY_LOGS;
export const gcloudProject = parsedEnv.TORO_GCLOUD_PROJECT;

export const mongooseDebug = parsedEnv.MONGOOSE_DEBUG;

export const MAX_FILE_UPLOAD_SIZE_MB = parsedEnv.MAX_FILE_UPLOAD_SIZE_MB;
export const MAX_FILE_UPLOAD_SIZE_BYTES = MAX_FILE_UPLOAD_SIZE_MB * 1024 * 1024;

// Different cookie names for non-production environments
export const authCookiePrefix =
  toroEnvironment === 'prod' ? 'toro-auth' : (`toro-auth-${toroEnvironment}` as const);

export { EXTENSION_ID, EXTENSION_ORIGIN };

type EnvHostConfig = Readonly<{
  dashboard: string;
  api: string;
  protocol: 'http' | 'https';
  additionalHosts?: readonly string[];
  // Domain for inbound otp emails for our integrations, e.g. bullhorn+xyz@otp.toroinsights.ai
  otpEmailHost: string | null;
}>;

const envHosts = {
  local: {
    dashboard: 'localhost:3000',
    api: 'localhost:8080',
    protocol: 'http',
    otpEmailHost: null,
  },
  dev: {
    dashboard: 'dev.toroinsights.ai',
    api: 'dev-api.toroinsights.ai',
    protocol: 'https',
    // Dev also allows requests from Vercel preview deployments
    additionalHosts: ['*-toro-translate.vercel.app'],
    otpEmailHost: 'otp-dev.toroinsights.ai',
  },
  test: {
    dashboard: 'dev.toroinsights.ai',
    api: 'dev-api.toroinsights.ai',
    protocol: 'https',
    otpEmailHost: null,
  },
  prod: {
    dashboard: 'app.toroinsights.ai',
    api: 'api.toroinsights.ai',
    protocol: 'https',
    otpEmailHost: 'otp.toroinsights.ai',
  },
} as const satisfies Record<ToroEnvironment, EnvHostConfig>;

const envUrls = R.mapValues(envHosts, (env) => {
  const { dashboard, api, protocol, otpEmailHost } = env;
  const additionalHosts = R.prop(env, 'additionalHosts') ?? [];
  return {
    dashboardHost: dashboard,
    dashboardUrl: `${protocol}://${dashboard}`,
    apiHost: api,
    apiUrl: `${protocol}://${api}`,
    additionalHosts: [EXTENSION_ORIGIN, ...additionalHosts],
    protocol,
    otpEmailHost,
  } as const;
});

const dashboardUrlsByEnv = R.mapValues(
  envHosts,
  ({ protocol, dashboard }) => `${protocol}://${dashboard}`
);

export const baseUrls = envUrls[toroEnvironment];
// Frontend base URL for the current environment, for generating links to the application
export const { dashboardUrl, apiUrl } = baseUrls;

type OtpHost = Extract<(typeof envHosts)[keyof typeof envHosts]['otpEmailHost'], string>;
export const otpHostsByEnv = R.mapValues(envHosts, R.prop('otpEmailHost'));
export const envForOtpHost = R.invert(R.pickBy(otpHostsByEnv, R.isString)) as Readonly<
  Record<OtpHost, ToroEnvironment>
>;
export const { otpEmailHost } = baseUrls;

export const AUTH_OTP_LENGTH = 6;
export const AUTH_OTP_EXPIRATION_MINUTES = 10;
